import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import TreeSelectorItem from './TreeSelectorItem';

import {
	Wrapper,
	Level,
	LevelTitle,
	TreeSelectorList,
	AllCheckbox,
} from './styles';

export default class TreeSelector extends Component {
	static propTypes = {
		data: PropTypes.object.isRequired,
		name: PropTypes.string.isRequired,
		value: PropTypes.arrayOf(PropTypes.string),
		onChange: PropTypes.func,
		levelNames: PropTypes.arrayOf(PropTypes.string),
		height: PropTypes.number,
	};

	static defaultProps = {
		onChange: () => {},
		value: null,
		levelNames: [],
		height: null,
	};

	state = {
		focusedItems: [],
		selectedItems: [],
	};

	componentDidUpdate = ({ value: oldValue }) => {
		const { value } = this.props;

		if (value && !isEqual(value, oldValue)) {
			this.setSelectedItemFromStore(value);
		}
	};

	componentDidMount = () => {
		const { value } = this.props;
		if (value) {
			this.setSelectedItemFromStore(value);
		}
	};

	getSelectedItems() {
		const { selectedItems } = this.state;
		return selectedItems;
	}

	getLevelsList() {
		const { data } = this.props;
		const { focusedItems } = this.state;
		const levels = [
			Object.values(data),
			...focusedItems.map(item => Object.values(item.children)),
		];

		return levels;
	}

	setSelectedItemFromStore(storeValue) {
		const { data } = this.props;

		const selectedItems = storeValue.map(id =>
			this.findItemInTreeById(id, data)
		);
		this.setState({ selectedItems });
	}

	findItemInTreeById = (id, treeItems) => {
		if (treeItems[id]) {
			return treeItems[id];
		}

		return Object.values(treeItems).reduce((acc, treeItem) => {
			if (acc) {
				return acc;
			}

			if (treeItem.children && Object.keys(treeItem.children).length > 0) {
				return this.findItemInTreeById(id, treeItem.children);
			}

			return null;
		}, null);
	};

	handleOnFocus = (item, focused) => {
		const { focusedItems } = this.state;

		if (focused) {
			const newFocusedItems = focusedItems.slice(0, item.level - 1);
			newFocusedItems[item.level - 1] = item;
			this.setState({ focusedItems: newFocusedItems });
		} else {
			this.setState({ focusedItems: focusedItems.slice(0, item.level - 1) });
		}
	};

	handleOnSelectAll = (item, selected) => {
		const { focusedItems } = this.state;

		this.handleOnSelect(focusedItems[item.level - 1], selected);
	};

	handleOnSelectAllFirstLevel = (_0, selected) => {
		const { data } = this.props;

		this.updateSelectedItems(selected ? Object.values(data) : []);
	};

	handleOnSelect = (item, selected) => {
		const selectedItems = this.getSelectedItems();

		if (selected) {
			let newSelectedItems = [item, ...selectedItems];

			// unselect all children - handle case when item is partially selected
			newSelectedItems = this.unselectAllChildren(item, newSelectedItems);

			// loop to select parent insead of all its children
			let loopItem = item;
			do {
				const groupItemsMap = loopItem.syblings;
				const groupItems = Object.values(groupItemsMap);
				const isWholeGroupSelected = this.isWholeGroupSelected(
					groupItems,
					newSelectedItems
				);
				// if after selecting this element whole group (all its sybling items) is selected
				if (isWholeGroupSelected && loopItem.level > 1) {
					// unselect the whole group
					newSelectedItems = newSelectedItems.filter(
						fItem => !groupItemsMap[fItem.id]
					);
					// and select the parent
					loopItem = loopItem.parent;
					newSelectedItems = [loopItem, ...newSelectedItems];
				} else {
					loopItem = null;
				}
			} while (loopItem);

			this.updateSelectedItems(newSelectedItems);
			// if parent is selected - and current item is not really selected
		} else if (this.checkIfParentIsInList(item, selectedItems)) {
			// loop to add syblings if the all option was selected
			let loopItem = item;
			let newSelectedItems = [...selectedItems];
			const filteringCb = fItem =>
				fItem !== loopItem.parent && fItem !== loopItem;
			do {
				// add all items from the parent to selected list
				newSelectedItems = [
					...Object.values(loopItem.syblings),
					...newSelectedItems,
					// filter out parent and current item
				].filter(filteringCb);
				// repeat for parent
				loopItem = loopItem.parent;
			} while (this.checkIfParentIsInList(loopItem, newSelectedItems));
			// remove current and its parent from selected items
			this.updateSelectedItems(newSelectedItems);
		} else {
			const newSelectedItems = [...selectedItems].filter(
				selectedItem => selectedItem !== item
			);

			this.updateSelectedItems(newSelectedItems);
		}
	};

	checkIfParentIsInList(item, list) {
		const { parent } = item;

		return (
			(parent &&
				!list.find(el => el.id === item.id) &&
				list.find(el => el.id === parent.id)) ||
			(parent && this.checkIfParentIsInList(parent, list))
		);
	}

	checkIfTreeContainsItem(tree, item) {
		if (tree[item.id]) {
			return true;
		}

		return !!Object.values(tree).find(
			treeItem =>
				treeItem.children &&
				this.checkIfTreeContainsItem(treeItem.children, item)
		);
	}

	unselectAllChildren(tree, list) {
		return list.filter(
			item => !this.checkIfTreeContainsItem(tree.children, item)
		);
	}

	updateSelectedItems(selectedItems) {
		const { name, onChange } = this.props;
		this.setState({ selectedItems });
		// allow selection to be visible before sending update to redux
		setTimeout(
			() =>
				onChange({
					target: {
						name,
						value: selectedItems.map(item => item.id.toString()),
					},
				}),
			0
		);
	}

	// item is partially selected when one of its children is selected
	isPartiallySelected(item) {
		// if it is in selected list - not partially selected
		if (this.isSelected(item)) {
			return false;
		}

		const selectedItems = this.getSelectedItems().filter(x => x);
		// if any of the selected items have this element as parent
		// - this element has to be partially selected
		const res = selectedItems.reduce((acc, selectedItem) => {
			// found earlier - skip further checks
			if (acc) {
				return true;
			}
			// check for parent matching
			// eslint-disable-next-line prefer-destructuring
			let parent = selectedItem.parent;
			while (parent && parent.id !== item.id) {
				// eslint-disable-next-line prefer-destructuring
				parent = parent.parent;
			}
			// if any of the parent matched, it will be truthy, otherwise null
			return !!parent;
		}, false);
		return res;
	}

	isSelected(item) {
		const selectedItems = this.getSelectedItems();
		let node = item;

		// eslint-disable-next-line no-loop-func
		while (node && !selectedItems.find(el => el.id === node.id)) {
			node = node.parent;
		}
		return !!node;
	}

	isSelectedAllFirstLevel() {
		const selectedItems = this.getSelectedItems();
		const { data } = this.props;
		return (
			Object.values(data).length === selectedItems.length &&
			selectedItems.reduce((acc, item) => acc && !!data[item.id], true)
		);
	}

	isWholeGroupSelected(groupItems, selectedItems) {
		return groupItems.reduce(
			(acc, groupItem) =>
				acc && selectedItems.find(el => el.id === groupItem.id),
			true
		);
	}

	render() {
		const { levelNames, height } = this.props;
		const { focusedItems } = this.state;
		const levels = this.getLevelsList();
		return (
			<Wrapper>
				{levels.map(
					(items, idx) =>
						items.length ? (
							/* eslint-disable react/no-array-index-key */
							<Level key={idx}>
								{/* eslint-enable react/no-array-index-key */}
								<LevelTitle>{levelNames[idx] || `Level ${idx + 1}`}</LevelTitle>
								<AllCheckbox
									label="all"
									item={{ level: idx }}
									selected={
										idx === 0
											? this.isSelectedAllFirstLevel()
											: this.isSelected(focusedItems[idx - 1])
									}
									onSelect={
										idx === 0
											? this.handleOnSelectAllFirstLevel
											: this.handleOnSelectAll
									}
								/>
								<TreeSelectorList style={{ height }}>
									{items.map(item => (
										<TreeSelectorItem
											key={item.id}
											label={item.name}
											item={item}
											focused={focusedItems[idx] === item}
											onFocus={this.handleOnFocus}
											selected={this.isSelected(item)}
											partiallySelected={this.isPartiallySelected(item)}
											onSelect={this.handleOnSelect}
										/>
									))}
								</TreeSelectorList>
							</Level>
						) : null
				)}
			</Wrapper>
		);
	}
}
